import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, api } from '../api/client.js'
import type { TaskStatus } from '../api/types.js'
import { AssigneePicker, type Assignment } from '../components/people.js'
import { useToast } from '../components/toast.js'

/** Only unfinished tasks can be ticked; done and closed ones are part of the proof. */
export const EDITABLE: TaskStatus[] = ['open', 'missed']

type StatusAction = '' | 'done' | 'skip' | 'final_missed'

const STATUS_LABEL: Record<Exclude<StatusAction, ''>, string> = {
  done: 'erledigt (Nachtrag)',
  skip: 'entfällt',
  final_missed: 'endgültig nicht erledigt',
}

/**
 * Mehrfachauswahl: tick several tasks, or all in the filter, then assign,
 * move or set a status in one go.
 */
export function BulkBar({ ids, onDone, onClear }: { ids: string[]; onDone: () => void; onClear: () => void }) {
  const toast = useToast()
  const [assignment, setAssignment] = useState<Assignment>({ assigneeId: null, assigneeTeamId: null })
  const [date, setDate] = useState('')
  const [status, setStatus] = useState<StatusAction>('')
  const [reason, setReason] = useState('')
  const run = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ updated: number; skipped: number }>('/api/occurrences/bulk', { method: 'POST', body: { ids, ...body } }),
    onSuccess: (result, body) => {
      const left =
        result.skipped === 0
          ? ''
          : body.action === 'done'
            ? `, ${result.skipped} ausgelassen (schon abgeschlossen, oder Textbaustein bzw. Messwert nötig, die einzeln nachtragen)`
            : `, ${result.skipped} schon abgeschlossene ausgelassen`
      toast(`${result.updated} ${result.updated === 1 ? 'Aufgabe' : 'Aufgaben'} geändert${left}.`)
      setStatus('')
      setReason('')
      onDone()
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Änderung fehlgeschlagen.', 'error'),
  })

  function applyStatus() {
    if (!status) return
    const what = `${ids.length} ${ids.length === 1 ? 'Aufgabe' : 'Aufgaben'} auf „${STATUS_LABEL[status]}“ setzen?`
    const extra =
      status === 'done'
        ? ' Sie gelten dann als vom Büro nachgetragen, ohne Fotos.'
        : status === 'skip'
          ? ' Sie bleiben als „entfallen“ im Nachweis.'
          : ' Sie werden nicht abgerechnet und nicht an Youtilly gemeldet.'
    if (!window.confirm(what + extra)) return
    run.mutate({ action: status, reason: reason.trim() || undefined })
  }

  return (
    <div className="bulk-bar" role="region" aria-label="Mehrfachauswahl">
      <span className="strong">{ids.length} ausgewählt</span>
      <div className="bulk-group">
        <AssigneePicker value={assignment} onChange={setAssignment} dense emptyLabel="Niemand" placeholder="Person oder Gruppe" />
        <button
          type="button"
          className="btn small"
          disabled={run.isPending || (!assignment.assigneeId && !assignment.assigneeTeamId)}
          onClick={() => run.mutate({ action: 'assign', ...assignment })}
        >
          Zuweisen
        </button>
      </div>
      <div className="bulk-group">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Neues Datum" />
        <button type="button" className="btn small" disabled={run.isPending || !date} onClick={() => run.mutate({ action: 'move', dueDate: date })}>
          Verschieben
        </button>
      </div>
      <div className="bulk-group">
        <select value={status} onChange={(e) => setStatus(e.target.value as StatusAction)} aria-label="Status setzen">
          <option value="">Status setzen</option>
          {(Object.keys(STATUS_LABEL) as Exclude<StatusAction, ''>[]).map((key) => (
            <option key={key} value={key}>
              {STATUS_LABEL[key]}
            </option>
          ))}
        </select>
        {(status === 'done' || status === 'final_missed') && (
          <input
            className="inline-input"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder={status === 'done' ? 'Woher kommt die Rückmeldung? (Pflicht)' : 'Begründung (freiwillig)'}
            aria-label="Begründung"
          />
        )}
        <button type="button" className="btn small" disabled={run.isPending || !status || (status === 'done' && !reason.trim())} onClick={applyStatus}>
          Setzen
        </button>
      </div>
      <button type="button" className="btn ghost small" onClick={onClear}>
        Auswahl aufheben
      </button>
    </div>
  )
}
